<template>
    <div class="admin-content-container">
        <div class="flex justify-between items-center mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">{{ pageDisplayName }}</h2>
        </div>

        <div v-if="loading" class="p-4 text-center">
            <Loader size="medium" />
        </div>

        <div v-else class="space-y-6">
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="font-header text-[1.5rem] font-bold mb-4">Page Description</h3>
                <p class="font-body text-body text-gray-600 mb-4">Edit the description that appears at the top of the {{
                    pageDisplayName }} page.</p>
                <div class="space-y-4">
                    <div>
                        <label class="block text-body font-header mb-1">Description</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="descriptionEditor" :content="formData.description"
                                @update:content="formData.description = $event"
                                placeholder="Write the page description here..." />
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-lg shadow-md">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="font-header text-[1.5rem] font-bold">Sections</h3>
                    <button type="button" @click="addSection"
                        class="px-3 py-1 text-[1.125rem] bg-green-700 text-white rounded hover:bg-green-800 font-header">
                        Add Section
                    </button>
                </div>
                <p class="font-body text-body text-gray-600 mb-4">Add subheadings with their own content to organize the
                    page.</p>
                <div v-for="(section, index) in formData.sections" :key="index"
                    class="mb-4 p-4 border border-gray-300 rounded-md">
                    <div class="flex justify-between items-center mb-2">
                        <label class="block text-body font-header">Section {{ index + 1 }}</label>
                        <button type="button" @click="removeSection(index)"
                            class="text-red-600 hover:text-red-700 text-[1.125rem] font-header">Remove</button>
                    </div>
                    <input v-model="section.title" type="text" placeholder="Section title (subheading)"
                        class="w-full px-3 py-2 mb-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                    <div class="border border-gray-300 rounded-md">
                        <RichTextEditor :ref="el => sectionEditors[index] = el" :content="section.content"
                            @update:content="section.content = $event"
                            placeholder="Write the section content here..." />
                    </div>
                </div>
                <p v-if="formData.sections.length === 0" class="font-body text-body text-gray-500">No sections added.
                    Click "Add Section" to create one.</p>
            </div>

            <div class="flex gap-2">
                <button @click="savePage" :disabled="saving"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem] disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ saving ? 'Saving...' : 'Save Page' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import { publicService } from '../../services/public'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import RichTextEditor from '../common/RichTextEditor.vue'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const props = defineProps({
    pageSlug: {
        type: String,
        required: true
    },
    pageDisplayName: {
        type: String,
        required: true
    },
    defaultDescription: {
        type: String,
        default: ''
    }
})

const { loading, setLoading } = useLoadingState(true)
const saving = ref(false)
const pageId = ref(null)
const descriptionEditor = ref(null)
const sectionEditors = ref([])

const formData = ref({
    description: convertLegacyQuillContent(''),
    sections: []
})

const getPageTitle = () => {
    const slugToTitle = {
        'products': 'Products and Services',
        'terms': 'Terms of Use',
        'privacy': 'Privacy Policy',
        'cookies': 'Cookies',
        'books': 'Books',
        'authors': 'Authors',
        'events': 'Events',
        'submissions': 'Submissions',
        'press': 'Press',
        'careers': 'Careers',
        'faq': 'FAQ'
    }
    return slugToTitle[props.pageSlug] || props.pageDisplayName
}

const loadPage = async () => {
    setLoading(true)
    try {
        const pageTitle = getPageTitle()
        const page = await publicService.getPage(pageTitle)
        pageId.value = page.id
        formData.value.description = convertLegacyQuillContent(page.description || '')
        formData.value.sections = page.sections && page.sections.length > 0
            ? page.sections.map(section => ({
                ...section,
                content: convertLegacyQuillContent(section.content || '')
            }))
            : []
    } catch (error) {
        console.error(`Failed to load page ${props.pageSlug}:`, error)
        pageId.value = null
        formData.value.description = convertLegacyQuillContent('')
        formData.value.sections = []
    } finally {
        setLoading(false)
    }
}

const addSection = () => {
    formData.value.sections.push({ title: '', content: convertLegacyQuillContent('') })
    sectionEditors.value.push(null)
}

const removeSection = (index) => {
    formData.value.sections.splice(index, 1)
    sectionEditors.value.splice(index, 1)
}

const savePage = async () => {
    try {
        saving.value = true
        const pageTitle = getPageTitle()
        const descriptionHtml = descriptionEditor.value
            ? descriptionEditor.value.getHTML?.() ?? ''
            : formData.value.description ?? ''
        const sectionsPayload = formData.value.sections.map((section, index) => {
            const sectionEditor = sectionEditors.value[index]
            const contentHtml = sectionEditor
                ? sectionEditor.getHTML?.() ?? ''
                : section.content ?? ''
            return { title: section.title, content: contentHtml }
        })
        const data = {
            title: pageTitle,
            description: descriptionHtml.trim() || null,
            sections: sectionsPayload
        }

        if (pageId.value) {
            await adminService.updatePage(pageId.value, data)
        } else {
            const created = await adminService.createPage(data)
            pageId.value = created.id
        }
        alert('Page saved successfully!')
    } catch (error) {
        console.error('Failed to save page:', error)
        alert('Failed to save page. Please try again.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadPage()
})
</script>

<style scoped>
.admin-content-container {
    width: 100%;
    min-height: 100vh;
    background-color: #F6F4F1;
    padding: 2rem;
    box-sizing: border-box;
}

:deep(.ql-editor) {
    min-height: 150px;
    line-height: 1.75;
    color: #253439;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Georgia', serif;
    direction: ltr !important;
    unicode-bidi: embed !important;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FAFAFA;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}
</style>
